import React, { useContext, useState, useEffect } from 'react';
import ProgressNotification from './ProgressNotification';
import Context from '../../../../Context/Context';
import uploadFunctions from './UploadFormComponents/Functions';

function UploadModal({ isOpen, setIsOpen, setVideos, setInProgress }) {
  const [showNotification, setShowNotification] = useState(false);
  const { playlists, fetchPlaylists } = useContext(Context);
  const [uploadStatus, setUploadStatus] = useState('uploading');
  const [showPlaylistSuggestions, setShowPlaylistSuggestions] = useState(false);
  const [filteredPlaylists, setFilteredPlaylists] = useState([]);
  const [partNoExists, setPartNoExists] = useState(false);
  const [shouldCreateNewPlaylist, setShouldCreateNewPlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [manualThumbnailSelected, setManualThumbnailSelected] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    songName: '',
    thumbnail: null,
    video: null,
    choreographer: '',
    videoType: '',
    partNo: 1,
    playlist: '',
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (formData.playlist) {
      const filtered = playlists.filter(playlist =>
        playlist.playlistName.toLowerCase().includes(formData.playlist.toLowerCase())
      );
      setFilteredPlaylists(filtered);
    } else {
      setFilteredPlaylists([]);
    }
  }, [formData.playlist, playlists]);

  useEffect(() => {
    if (formData.playlist && formData.partNo) {
      const selectedPlaylist = playlists.find(
        playlist => playlist.playlistName === formData.playlist
      );

      if (selectedPlaylist && selectedPlaylist.partNoInclude) {
        const checkPartExists = (partNo) => {
          return selectedPlaylist.partNoInclude.includes(partNo.toString());
        };

        if (checkPartExists(formData.partNo)) {
          let nextPartNo = formData.partNo;
          while (checkPartExists(nextPartNo)) {
            nextPartNo++;
          }
          
          setFormData(prev => ({
            ...prev,
            partNo: nextPartNo
          }));
        }
        
        setPartNoExists(false);
      }
    }
  }, [formData.partNo, formData.playlist, playlists]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'partNo' || name === 'videoType') {
      return;
    } else if (name === 'thumbnail' && files?.[0]) {
      const file = files[0];
      const maxSize = 5 * 1024 * 1024;
      
      if (file.size > maxSize) {
        setThumbnailError('Thumbnail file size must be less than 5MB');
        setFormData(prevData => ({
          ...prevData,
          thumbnail: null
        }));
        return;
      } else {
        setThumbnailError(null);
        setManualThumbnailSelected(true);
        setFormData(prevData => ({
          ...prevData,
          thumbnail: file
        }));
      }
    } else if (name === 'video' && files?.[0]) {
      setFormData(prevData => ({
        ...prevData,
        video: files[0]
      }));

      if (!manualThumbnailSelected) {
        generateThumbnail(files[0]);
      }
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: value
      }));
    }

    if (name === 'playlist') {
      setShowPlaylistSuggestions(true);
      handleNewPlaylistNameChange(e);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      songName: '',
      thumbnail: null,
      video: null,
      choreographer: '',
      videoType: '',
      partNo: 1,
      playlist: '',
    });
    setUploadProgress(0);
    setError(null);
    setThumbnailError(null);
    setPartNoExists(false);
    setShouldCreateNewPlaylist(false);
    setManualThumbnailSelected(false);
  };

  const generateThumbnail = (videoFile) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    video.src = URL.createObjectURL(videoFile);

    video.onloadedmetadata = () => {
      const captureTime = video.duration / 3;
      video.currentTime = captureTime;
    };

    video.onseeked = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        const file = new File([blob], 'thumbnail.png', { type: 'image/png' });
        setFormData((prevData) => ({
          ...prevData,
          thumbnail: file,
        }));
      }, 'image/png');
      video.remove();
    };
  };

  const handlePlaylistSelect = (playlistName) => {
    const selectedPlaylist = playlists.find(
      playlist => playlist.playlistName === playlistName
    );

    const nextPartNo = selectedPlaylist ? selectedPlaylist.videoCount + 1 : 1;

    setFormData(prev => ({
      ...prev,
      playlist: playlistName,
      partNo: nextPartNo,
      videoType: playlistName
    }));

    setShowPlaylistSuggestions(false);
    setShouldCreateNewPlaylist(false);
  };

  const handleNewPlaylistNameChange = (e) => {
    const { value } = e.target;
    setNewPlaylistName(value);

    const existingPlaylist = playlists.find(
      playlist => playlist.playlistName.toLowerCase() === value.toLowerCase()
    );

    if (!existingPlaylist) {
      setShouldCreateNewPlaylist(true);
      setFormData(prev => ({
        ...prev,
        playlist: value,
        partNo: 1,
        videoType: value
      }));
    } else {
      setShouldCreateNewPlaylist(false);
      const nextPartNo = existingPlaylist.videoCount + 1;
      setFormData(prev => ({
        ...prev,
        playlist: value,
        partNo: nextPartNo,
        videoType: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    setInProgress(true);
    setUploadProgress(0);
    setShowNotification(true);
    setUploadStatus('uploading');

    if (!formData.video) {
      setError('Please upload both a video and a thumbnail.');
      return;
    }

    setIsOpen(false);

    try {
      const uploadedVideoData = await uploadFunctions.handleCompleteUpload(formData, setUploadProgress);
      setUploadStatus('completed');
      setTimeout(() => {
        setShowNotification(false);
        resetForm();
      }, 2000);

      setVideos((prevVideos) => [uploadedVideoData.data, ...prevVideos]);
      fetchPlaylists();
    } catch (error) {
      console.error('Error during video upload:', error);
      setError(error.message || 'An error occurred during upload. Please try again.');
      setUploadProgress(0);
      setUploadStatus('error');
    }
  };

  if (!isOpen) {
    return showNotification ? (
      <ProgressNotification
        progress={uploadProgress}
        status={uploadStatus}
        setInProgress={setInProgress}
        onClose={() => {
          setShowNotification(false);
          resetForm();
        }}
      />
    ) : null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Upload Video</h2>
          <button
            onClick={() => {
              setIsOpen(false);
              resetForm();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Row 1: Title and Song Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter video title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Song Name</label>
                <input
                  type="text"
                  name="songName"
                  value={formData.songName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter song name"
                />
              </div>
            </div>

            {/* Row 2: Choreographer and Playlist */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Choreographer</label>
                <input
                  type="text"
                  name="choreographer"
                  value={formData.choreographer}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter choreographer name"
                />
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Playlist</label>
                <input
                  type="text"
                  name="playlist"
                  value={formData.playlist}
                  onChange={handleChange}
                  onFocus={() => setShowPlaylistSuggestions(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter or select playlist"
                />
                
                {showPlaylistSuggestions && filteredPlaylists.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredPlaylists.map((playlist, index) => (
                      <div
                        key={index}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => handlePlaylistSelect(playlist.playlistName)}
                      >
                        <div className="font-medium text-gray-900">{playlist.playlistName}</div>
                        <div className="text-sm text-gray-500">{playlist.videoCount} videos</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {shouldCreateNewPlaylist && (
                  <p className="text-sm text-green-600 mt-1">
                    New playlist will be created
                  </p>
                )}
              </div>
            </div>

            {/* Row 3: Video and Thumbnail Upload */}
            <div className="grid grid-cols-2 gap-4">
              {/* Video Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Video</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg  text-center hover:border-gray-400 transition-colors h-32 flex items-center justify-center">
                  <input
                    type="file"
                    name="video"
                    onChange={handleChange}
                    accept="video/*"
                    className="hidden"
                    id="video-upload"
                  />
                  <label htmlFor="video-upload" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                    {formData.video ? (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <div className="w-full h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden mb-2">
                          <video 
                            src={URL.createObjectURL(formData.video)} 
                            className="w-full h-full object-cover rounded-lg"
                            muted
                            onLoadedMetadata={(e) => {
                              e.target.currentTime = e.target.duration / 3;
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <svg className="w-6 h-6 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-xs text-gray-600">Upload video</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Thumbnail Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-gray-400 transition-colors h-32 flex items-center justify-center">
                  <input
                    type="file"
                    name="thumbnail"
                    onChange={handleChange}
                    accept="image/*"
                    className="hidden"
                    id="thumbnail-upload"
                  />
                  <label htmlFor="thumbnail-upload" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                    {formData.thumbnail ? (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        {formData.thumbnail.type.startsWith('image/') ? (
                          <div className="w-full h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden mb-2">
                            <img 
                              src={URL.createObjectURL(formData.thumbnail)} 
                              alt="Thumbnail preview" 
                              className="w-full h-full object-cover rounded-lg"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-20 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <svg className="w-6 h-6 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-xs text-gray-600">Upload thumbnail</p>
                      </div>
                    )}
                  </label>
                </div>
                {thumbnailError && (
                  <p className="text-red-500 text-xs mt-1">{thumbnailError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  resetForm();
                }}
                className="w-full bg-gray-200 text-gray-700 py-2.5 px-4 rounded-lg font-medium hover:bg-gray-300 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full bg-black text-white py-2.5 px-4 rounded-lg font-medium hover:bg-gray-800 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Upload Video
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UploadModal;